import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Target,
  BarChart3,
  Trash2,
  Loader2,
  Download,
  FileText,
  BarChart,
  AlertCircle
} from 'lucide-react'
import { ManualTestTrade, ManualTestStats, ManualTestFormData } from '../types'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n'
import { exportToCSV, exportToPDF, validateTimeOrder } from '../utils/exportUtils'
import ManualTestCharts from './ManualTestCharts'
import { getManualTestTrades, getManualTestStats, submitManualTestTrade, updateManualTestTrade, deleteManualTestTrade } from '../lib/api'
import Toast from './Toast'
import ConfirmDialog from './ConfirmDialog'

const manualTestSchema = z.object({
  entry_time: z.string().min(1, '请选择入场时间'),
  exit_time: z.string().min(1, '请选择出场时间'),
  direction: z.enum(['long', 'short'], { required_error: '请选择交易方向' }),
  entry_price: z.number().positive('入场价格必须大于0'),
  exit_price: z.number().positive('出场价格必须大于0'),
  size: z.number().positive('交易数量必须大于0'),
  initial_risk: z.number().positive('初始风险必须大于0'),
  reason_entry: z.string().min(1, '请输入入场理由'),
  reason_exit: z.string().min(1, '请输入出场理由'),
  manual_override: z.boolean(),
  notes: z.string().optional()
})

type ManualTestFormSchema = z.infer<typeof manualTestSchema>

interface ManualTestTradesProps {
  strategyId: string
}

export default function ManualTestTrades({ strategyId }: ManualTestTradesProps) {
  const t = useI18n('zh').manualTest
  const [trades, setTrades] = useState<ManualTestTrade[]>([])
  const [stats, setStats] = useState<ManualTestStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [showCharts, setShowCharts] = useState(false)
  const [editingTrade, setEditingTrade] = useState<ManualTestTrade | null>(null)
  const [deletingTrade, setDeletingTrade] = useState<ManualTestTrade | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [filterDirection, setFilterDirection] = useState<'all' | 'long' | 'short'>('all')
  const [filterPnL, setFilterPnL] = useState<'all' | 'profit' | 'loss'>('all')
  const [filterRValueRange, setFilterRValueRange] = useState<string | null>(null)
  const [strategyName, setStrategyName] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<ManualTestFormSchema>({
    resolver: zodResolver(manualTestSchema),
    defaultValues: {
      direction: 'long',
      manual_override: false,
      entry_time: new Date().toISOString().slice(0, 16),
      exit_time: new Date().toISOString().slice(0, 16)
    }
  })

  const watchedValues = watch()

  // 计算实时盈亏和R值
  const calculateFormPnL = () => {
    const { entry_price, exit_price, size, direction, initial_risk } = watchedValues
    if (!entry_price || !exit_price || !size || !initial_risk) return { pnl: 0, pnlR: 0 }

    let pnl = 0
    if (direction === 'long') {
      pnl = (exit_price - entry_price) * size
    } else {
      pnl = (entry_price - exit_price) * size
    }

    const pnlR = initial_risk > 0 ? pnl / initial_risk : 0
    return { pnl, pnlR }
  }

  const { pnl, pnlR } = calculateFormPnL()

  // 获取交易记录
  const fetchTrades = async () => {
    setLoading(true)
    try {
      setError(null)
      const result = await getManualTestTrades(strategyId)
      setTrades(result || [])
    } catch (err) {
      console.error('Error fetching trades:', err)
      setTrades([])
    } finally {
      setLoading(false)
    }
  }

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const result = await getManualTestStats(strategyId)
      setStats(result)
    } catch (err) {
      console.error('Error fetching stats:', err)
      setStats({
        total_trades: 0,
        winning_trades: 0,
        losing_trades: 0,
        win_rate: 0,
        avg_r_value: 0,
        max_r_value: 0,
        min_r_value: 0,
        max_drawdown: 0,
        total_pnl: 0,
        avg_pnl_per_trade: 0,
        long_trades: 0,
        short_trades: 0,
        long_win_rate: 0,
        short_win_rate: 0,
        avg_trade_duration: 0,
        manual_override_count: 0,
        manual_override_rate: 0
      })
    }
  }

  // 获取策略名称
  const fetchStrategyName = async () => {
    try {
      const { data, error } = await supabase
        .from('strategies')
        .select('name')
        .eq('id', strategyId)
        .single()

      if (error) throw error
      setStrategyName(data?.name || '')
    } catch (err) {
      console.error('Error fetching strategy name:', err)
    }
  }

  useEffect(() => {
    fetchTrades()
    fetchStats()
    fetchStrategyName()
  }, [strategyId])

  // 提交交易记录
  const onSubmit = async (data: ManualTestFormData) => {
    setLoading(true)
    setError(null)
    try {
      // 验证时间顺序
      if (!validateTimeOrder(data.entry_time, data.exit_time)) {
        setError('出场时间必须晚于入场时间')
        setLoading(false)
        return
      }

      const { pnl, pnlR } = calculateFormPnL()
      
      const tradeData = {
        strategy_id: strategyId,
        entry_time: data.entry_time,
        exit_time: data.exit_time,
        direction: data.direction,
        entry_price: data.entry_price,
        exit_price: data.exit_price,
        size: data.size,
        initial_risk: data.initial_risk,
        pnl_amount: pnl,
        pnl_r: pnlR,
        reason_entry: data.reason_entry,
        reason_exit: data.reason_exit,
        manual_override: data.manual_override,
        notes: data.notes
      }

      if (editingTrade) {
        // 更新现有交易记录
        await updateManualTestTrade(editingTrade.id, tradeData)
        setToast({ type: 'success', message: '交易记录更新成功' })
      } else {
        // 创建新交易记录
        await submitManualTestTrade(tradeData)
        setToast({ type: 'success', message: '交易记录添加成功' })
      }

      reset()
      setShowForm(false)
      setEditingTrade(null)
      
      await fetchTrades()
      await fetchStats()
    } catch (error) {
      console.error('Error submitting trade record:', error)
      
      let errorMessage = '提交失败: '
      if (error instanceof Error) {
        errorMessage += error.message
      } else if (typeof error === 'string') {
        errorMessage += error
      } else {
        errorMessage += '未知错误'
      }
      
      setError(errorMessage)
      setToast({ type: 'error', message: '提交失败' })
    } finally {
      setLoading(false)
    }
  }

  const handleEditTrade = (trade: ManualTestTrade) => {
    setEditingTrade(trade)
    reset({
      entry_time: trade.entry_time.slice(0, 16),
      exit_time: trade.exit_time.slice(0, 16),
      direction: trade.direction,
      entry_price: trade.entry_price,
      exit_price: trade.exit_price,
      size: trade.size,
      initial_risk: trade.initial_risk,
      reason_entry: trade.reason_entry,
      reason_exit: trade.reason_exit,
      manual_override: trade.manual_override,
      notes: trade.notes || ''
    })
    setShowForm(true)
  }

  const handleDeleteTrade = async () => {
    if (!deletingTrade) return

    try {
      setLoading(true)
      setError(null)
      
      await deleteManualTestTrade(deletingTrade.id)
      await fetchTrades()
      await fetchStats()
      setDeletingTrade(null)
      setToast({ type: 'success', message: '交易记录删除成功' })
    } catch (error) {
      console.error('删除交易记录失败:', error)
      setError('删除交易记录失败，请稍后重试')
      setToast({ type: 'error', message: '删除交易记录失败' })
    } finally {
      setLoading(false)
    }
  }

  // 导出功能
  const handleExportCSV = () => {
    if (trades.length === 0) {
      return
    }
    exportToCSV(trades, strategyName)
  }

  const handleExportPDF = () => {
    if (trades.length === 0 || !stats) {
      return
    }
    exportToPDF(trades, stats, strategyName)
  }

  // R值范围筛选处理
  const handleRValueRangeClick = (range: string) => {
    setFilterRValueRange(range)
  }

  // 过滤交易记录
  const filteredTrades = trades.filter(trade => {
    if (filterDirection !== 'all' && trade.direction !== filterDirection) return false
    if (filterPnL === 'profit' && trade.pnl_amount <= 0) return false
    if (filterPnL === 'loss' && trade.pnl_amount >= 0) return false
    
    // R值范围筛选
    if (filterRValueRange) {
      const rValue = trade.pnl_r
      const rangeMap: Record<string, { min: number; max: number }> = {
        '-∞ ~ -2': { min: -Infinity, max: -2 },
        '-2 ~ -1': { min: -2, max: -1 },
        '-1 ~ 0': { min: -1, max: 0 },
        '0 ~ 1': { min: 0, max: 1 },
        '1 ~ 2': { min: 1, max: 2 },
        '2 ~ ∞': { min: 2, max: Infinity }
      }
      const range = rangeMap[filterRValueRange]
      if (range && (rValue < range.min || rValue >= range.max)) return false
    }
    
    return true
  })

  return (
    <div className="space-y-6">
      {/* 统计摘要 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              <span className="ml-2 text-sm text-gray-600">{t.totalTrades}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_trades}</p>
          </div>
          <div className="card p-4">
            <div className="flex items-center">
              <Target className="w-5 h-5 text-green-600" />
              <span className="ml-2 text-sm text-gray-600">{t.winRate}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{(stats.win_rate * 100).toFixed(1)}%</p>
          </div>
          <div className="card p-4">
            <div className="flex items-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="ml-2 text-sm text-gray-600">{t.avgRValue}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avg_r_value.toFixed(2)}</p>
          </div>
          <div className="card p-4">
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-purple-600" />
              <span className="ml-2 text-sm text-gray-600">{t.totalPnL}</span>
            </div>
            <p className={`text-2xl font-bold ${stats.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.total_pnl >= 0 ? '+' : ''}{stats.total_pnl.toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* 操作栏 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t.addTradeRecord}
          </button>
          
          <button
            onClick={() => setShowCharts(!showCharts)}
            className="btn-secondary flex items-center whitespace-nowrap"
          >
            <BarChart className="w-4 h-4 mr-2" />
            {showCharts ? '隐藏图表' : '显示图表'}
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* 导出按钮 */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="btn-secondary flex items-center text-sm whitespace-nowrap"
              disabled={trades.length === 0}
            >
              <Download className="w-4 h-4 mr-1" />
              {t.exportCSV}
            </button>
            <button
              onClick={handleExportPDF}
              className="btn-secondary flex items-center text-sm whitespace-nowrap"
              disabled={trades.length === 0 || !stats}
            >
              <FileText className="w-4 h-4 mr-1" />
              {t.exportPDF}
            </button>
          </div>

          {/* 筛选器 */}
          <select
            value={filterDirection}
            onChange={(e) => setFilterDirection(e.target.value as any)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">{t.allDirections}</option>
            <option value="long">{t.long}</option>
            <option value="short">{t.short}</option>
          </select>
          <select
            value={filterPnL}
            onChange={(e) => setFilterPnL(e.target.value as any)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">{t.allPnL}</option>
            <option value="profit">{t.profit}</option>
            <option value="loss">{t.loss}</option>
          </select>
          
          {/* R值范围筛选 */}
          {filterRValueRange && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">R值: {filterRValueRange}</span>
              <button
                onClick={() => setFilterRValueRange(null)}
                className="text-red-500 hover:text-red-700 text-xs"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 添加交易记录表单 */}
      {showForm && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingTrade ? '编辑交易记录' : t.addTradeRecord}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.entryTime}
                </label>
                <input
                  {...register('entry_time')}
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.entry_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.entry_time.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.exitTime}
                </label>
                <input
                  {...register('exit_time')}
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.exit_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.exit_time.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.direction}
                </label>
                <select
                  {...register('direction')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="long">{t.long}</option>
                  <option value="short">{t.short}</option>
                </select>
                {errors.direction && (
                  <p className="mt-1 text-sm text-red-600">{errors.direction.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  初始风险
                </label>
                <input
                  {...register('initial_risk', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  placeholder="输入初始风险金额"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.initial_risk && (
                  <p className="mt-1 text-sm text-red-600">{errors.initial_risk.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.entryPrice}
                </label>
                <input
                  {...register('entry_price', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  placeholder="入场价格"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.entry_price && (
                  <p className="mt-1 text-sm text-red-600">{errors.entry_price.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.exitPrice}
                </label>
                <input
                  {...register('exit_price', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  placeholder="出场价格"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.exit_price && (
                  <p className="mt-1 text-sm text-red-600">{errors.exit_price.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.positionSize}
                </label>
                <input
                  {...register('size', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  placeholder="交易数量"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.size && (
                  <p className="mt-1 text-sm text-red-600">{errors.size.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  {...register('manual_override')}
                  type="checkbox"
                  id="manual_override"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="manual_override" className="ml-2 text-sm text-gray-700">
                  {t.manualOverride}
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                入场理由
              </label>
              <textarea
                {...register('reason_entry')}
                rows={2}
                placeholder="描述入场理由..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.reason_entry && (
                <p className="mt-1 text-sm text-red-600">{errors.reason_entry.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                出场理由
              </label>
              <textarea
                {...register('reason_exit')}
                rows={2}
                placeholder="描述出场理由..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.reason_exit && (
                <p className="mt-1 text-sm text-red-600">{errors.reason_exit.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.addNote}
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                placeholder="添加备注信息（支持Markdown）..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* 实时计算显示 */}
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">{t.estimatedPnL}:</span>
                  <span className={`ml-2 font-semibold ${pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">{t.estimatedRValue}:</span>
                  <span className={`ml-2 font-semibold ${pnlR >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {pnlR >= 0 ? '+' : ''}{pnlR.toFixed(2)}R
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary"
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center whitespace-nowrap"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {editingTrade ? '更新记录' : t.addRecord}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 交易记录列表 */}
      <div className="card">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">{t.tradeRecords}</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" />
            <p className="mt-2 text-gray-600">{t.loading}</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <AlertCircle className="w-10 h-10 mx-auto mb-4" />
            {error}
          </div>
        ) : filteredTrades.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t.noTradeRecords}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t.time}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t.direction}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t.price}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t.pnl}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t.rValue}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t.operation}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTrades.map((trade) => (
                  <tr key={trade.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3 text-sm">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {new Date(trade.entry_time).toLocaleDateString()}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs">
                          {new Date(trade.entry_time).toLocaleTimeString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        trade.direction === 'long' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {trade.direction === 'long' ? (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        ) : (
                          <TrendingDown className="w-3 h-3 mr-1" />
                        )}
                        {trade.direction === 'long' ? t.long : t.short}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>
                        <div className="text-gray-900 dark:text-white">{t.entryPrice}: {trade.entry_price}</div>
                        <div className="text-gray-900 dark:text-white">{t.exitPrice}: {trade.exit_price}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`font-medium ${
                        trade.pnl_amount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {trade.pnl_amount >= 0 ? '+' : ''}{trade.pnl_amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`font-medium ${
                        trade.pnl_r >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {trade.pnl_r >= 0 ? '+' : ''}{trade.pnl_r.toFixed(2)}R
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                                              <button
                          onClick={() => handleEditTrade(trade)}
                          className="text-blue-600 hover:text-blue-900 mr-2"
                          title="编辑"
                        >
                          ✏️
                        </button>
                      <button
                        onClick={() => setDeletingTrade(trade)}
                        className="text-red-600 hover:text-red-900"
                        title={t.delete}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 图表展示 */}
      {showCharts && (
        <ManualTestCharts 
          trades={filteredTrades} 
          onRValueRangeClick={handleRValueRangeClick}
        />
      )}

      {/* 删除确认对话框 */}
      {deletingTrade && (
        <ConfirmDialog
          isOpen={!!deletingTrade}
          onCancel={() => setDeletingTrade(null)}
          onConfirm={handleDeleteTrade}
          title="确认删除"
          message={`确定要删除交易记录吗？此操作不可逆。`}
          confirmText="删除"
          cancelText="取消"
          type="danger"
        />
      )}

      {/* 提示信息 */}
      {toast && (
        <Toast
          message={toast.message}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
} 